import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, LuxuryButton } from "@/components/lux";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, AlertTriangle } from "lucide-react";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50";

export default function VirtualBusiness() {
  const { data, reload } = useFetch("/admin/virtual-business");
  const [f, setF] = useState({ member_id: "", side: "left", amount: "", reason: "", matching_eligible: true, qualification_eligible: true });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/admin/virtual-business", { ...f, amount: Number(f.amount) });
      toast.success("Promotional volume added"); setF({ ...f, member_id: "", amount: "", reason: "" }); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageTitle title="Virtual / Promotional Business" subtitle="Add configurable promotional volume — always tracked separately from real business" />
      <div className="flex items-start gap-3 rounded-xl border border-amber-400/20 bg-amber-400/5 p-4 text-sm text-amber-200">
        <AlertTriangle size={18} className="mt-0.5" />
        Promotional volume must never be represented as real customer business. Every entry is recorded in an immutable audit log with admin ID, reason and timestamp.
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <GlassCard className="p-6">
          <div className="mb-4 flex items-center gap-2"><Sparkles size={18} className="text-[var(--gold)]" /><h3 className="font-semibold text-white">Add Volume</h3></div>
          <form onSubmit={submit} className="space-y-3">
            <input className={inp} placeholder="Member ID (e.g. 100002)" value={f.member_id} onChange={upd("member_id")} data-testid="vb-member" required />
            <div className="grid grid-cols-2 gap-3">
              {["left", "right"].map((s) => (
                <button key={s} type="button" onClick={() => setF({ ...f, side: s })} className={`btn3d ${f.side === s ? "btn3d-gold" : "btn3d-dark"} py-2.5 text-sm capitalize`} data-testid={`vb-side-${s}`}>{s}</button>
              ))}
            </div>
            <input className={inp} type="number" step="any" placeholder="Volume amount" value={f.amount} onChange={upd("amount")} data-testid="vb-amount" required />
            <textarea className={inp} placeholder="Reason (audit note)" value={f.reason} onChange={upd("reason")} data-testid="vb-reason" required />
            <label className="flex items-center gap-2 text-sm text-[var(--text-dim)]">
              <input type="checkbox" checked={f.matching_eligible} onChange={(e) => setF({ ...f, matching_eligible: e.target.checked })} data-testid="vb-matching" /> Matching eligible
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--text-dim)]">
              <input type="checkbox" checked={f.qualification_eligible} onChange={(e) => setF({ ...f, qualification_eligible: e.target.checked })} /> Qualification eligible
            </label>
            <LuxuryButton type="submit" className="w-full" disabled={busy} data-testid="vb-submit">Add Promotional Volume</LuxuryButton>
          </form>
        </GlassCard>

        <GlassCard className="p-6">
          <h3 className="mb-4 font-semibold text-white">Promotional Volume Log</h3>
          {data?.records?.length ? (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-[var(--text-dim)]"><tr className="border-b border-white/10"><th className="py-2">Member</th><th className="py-2">Side</th><th className="py-2">Amount</th><th className="py-2">Reason</th></tr></thead>
                <tbody>
                  {data.records.map((r, i) => (
                    <tr key={i} className="row-hover border-b border-white/5">
                      <td className="py-2.5 text-white">{r.member_id}</td><td className="py-2.5 capitalize">{r.side}</td>
                      <td className="py-2.5 gold-text">${money(r.amount)}</td><td className="py-2.5 text-[var(--text-dim)]">{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-[var(--text-dim)]">No promotional volume added yet.</p>}
        </GlassCard>
      </div>
    </div>
  );
}
